import { useLingui } from '@lingui/react/macro';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { rejectSessionMember } from '#modules/finder/features/posts/model/sessions';
import { invalidateFor } from '#features/realtime/cacheInvalidation';
import { toaster } from '#features/ui/components/Toaster';
import { toVoiceMembers } from '#features/voice/hooks/useVoiceRoom';
import {
  joinVoiceRoom,
  muteInVoiceRoom,
  VoiceRequestError,
  voiceRoomQueryKey,
} from '#features/voice/model/voice';
import { forgetVoiceRoomSwitch, markVoiceRoomSwitch } from '#features/voice/model/voiceRoomSwitch';
import { readLastMicOn, useVoiceStore } from '#features/voice/model/voiceStore';

/**
 * Вход в голос, исключение из сессии и заглушение участника.
 *
 * Выход живёт не здесь, а в {@link useVoiceRoom}: он обязан сперва разорвать медиа и только потом
 * сообщить серверу, иначе человек ещё несколько секунд слышен остальным после нажатия «выйти».
 */
export const useVoiceActions = () => {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const setConnecting = useVoiceStore((s) => s.setConnecting);
  const setMembers = useVoiceStore((s) => s.setMembers);
  const setError = useVoiceStore((s) => s.setError);
  const reset = useVoiceStore((s) => s.reset);
  const [pending, setPending] = useState(false);

  const join = useCallback(
    async (
      sessionId: string,
      context?: { gameName?: string | null; hostName?: string | null; micEnabled?: boolean }
    ) => {
      setPending(true);
      /*
        Это возврат, а не новый вход: связь оборвалась или голос забрало другое устройство, и
        человек жмёт «Вернуться» / «Вернуть сюда». Микрофон тогда возвращаем тот, что был, – иначе
        выключенный включался бы сам (прогон на телефоне 02.10.2026). Код отказа знать надо до
        сброса, поэтому смотрим его здесь.
      */
      const before = useVoiceStore.getState();
      const returning =
        before.lastErrorSessionId === sessionId &&
        (before.lastError === 'voice.other.tab' || before.lastError === 'voice.connection.lost');
      setError(null);
      /*
        Уже сидим в другой комнате - значит это переход, и сервер выведет нас из прежней. Метку
        ставим до запроса: разрыв старой комнаты приезжает асинхронно и может опередить ответ.
      */
      const leaving = useVoiceStore.getState().room;
      if (leaving && leaving.sessionId !== sessionId) {
        markVoiceRoomSwitch(leaving.sessionId);
      }
      try {
        const room = await joinVoiceRoom(sessionId);
        if (!room.livekitUrl || !room.token) {
          // Сервер согласился, но не выдал ключей — считать это успехом нельзя, иначе виджет
          // повиснет в «подключаюсь» навсегда.
          throw new VoiceRequestError('voice.unavailable');
        }

        setConnecting({
          sessionId: room.sessionId,
          postId: room.postId,
          hostId: room.hostId,
          livekitUrl: room.livekitUrl,
          token: room.token,
          maxParticipants: room.maxParticipants,
          gameName: context?.gameName ?? null,
          hostName: context?.hostName ?? null,
        });
        useVoiceStore.getState().setMicEnabled(
          context?.micEnabled ?? (returning ? readLastMicOn() : true)
        );
        setMembers(toVoiceMembers(room.participants));
        queryClient.setQueryData(voiceRoomQueryKey(sessionId), room);
        return room;
      } catch (error) {
        const code = error instanceof VoiceRequestError ? error.errorCode : 'global.error';
        /*
          Гасим состояние только если рвётся та самая комната, в которую шли.

          Карточка чужой сессии зовёт в голос прямо из ленты, и человек часто жмёт её, уже сидя
          в разговоре другой сессии. Безусловный `reset` в этом случае выбрасывал его из живого
          разговора за отказ по чужой комнате: команда видела, что он вышел, а он не понимал,
          почему его перестали слышать. Отказ во входе никуда не должен девать то, что уже
          работает; переключение - дело удачного входа, он ставит новую комнату сам.

          Порядок остался прежним: `reset` очищает и код отказа тоже, поэтому сначала гасим
          состояние и только потом записываем причину.
        */
        // Переход не состоялся - метку снимаем, иначе она соврёт на настоящем выгоне.
        forgetVoiceRoomSwitch();

        const live = useVoiceStore.getState().room;
        if (!live || live.sessionId === sessionId) {
          reset();
        }
        setError(code, sessionId);
        throw error;
      } finally {
        setPending(false);
      }
    },
    [queryClient, reset, setConnecting, setError, setMembers]
  );

  /**
   * Модерация обязана говорить, когда не сработала.
   *
   * <p>До 27.08 отказ здесь пропадал: вызовы уходили через `void`, и хост, нажавший «заглушить»
   * при недоступном медиасервере, видел ровно то же, что при успехе, — ничего. Человек в это
   * время продолжал говорить. Проверено на dev: трек в LiveKit оставался `muted=false`.
   */
  const moderationFailed = useCallback(
    (removing = false) => {
      toaster.create({
        title: t`post:voice.moderationFailed`,
        description: removing
          ? t`Could not remove the participant. Please try again.`
          : t`post:voice.moderationFailedDetail`,
        type: 'error',
        duration: 6000,
      });
    },
    [t]
  );

  const removeFromSession = useCallback(
    async (postId: string, sessionId: string, userId: string) => {
      try {
        const result = await rejectSessionMember({ sessionId, userId });
        if (result?.__typename !== 'Operation') {
          throw new Error('session.member.remove.failed');
        }
      } catch (error) {
        moderationFailed(true);
        throw error;
      }
      // Собственное действие автора может не вернуться сокетом. Обновляем и состав, и голос.
      await Promise.all([
        invalidateFor(queryClient, 'SESSION_MEMBER_REMOVED', {
          parentId: postId,
          targetId: sessionId,
        }),
        queryClient.invalidateQueries({ queryKey: voiceRoomQueryKey(sessionId) }),
        queryClient.invalidateQueries({ queryKey: ['chats'] }),
      ]);
    },
    [moderationFailed, queryClient]
  );

  const mute = useCallback(
    async (sessionId: string, userId: string) => {
      try {
        await muteInVoiceRoom(sessionId, userId);
      } catch (error) {
        moderationFailed();
        throw error;
      }
    },
    [moderationFailed]
  );

  return { join, removeFromSession, mute, pending };
};
